import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { KAL_DIALOG_DATA, KalDialogRef, KalSnackbarService } from '@kalidea/kaligraphi';
import { InjectorProviderService } from 'src/app/_core/services/injector.provider.service';
import { UserState } from 'src/app/_user/store/user/user.state';
import { UserUpdateApplicationParametersAction } from 'src/app/_user/store/user/user.action';
import { tap } from 'rxjs/operators';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { ParametresUserAppliModelDto } from 'src/graphql/generated-types';

export interface SharedApplicationParametersCategory {
    label: string;
    applicationParametersList: SharedApplicationParameters[];
}

export interface SharedApplicationParameters {
    selected?: boolean;
    code: string;
    label: string;
}

export interface SharedApplicationParametersSelectionDialogData {
    params: (SharedApplicationParametersCategory | SharedApplicationParameters)[];
    activityId?: number;
}

@Component({
    selector: 'app-shared-application-parameters-selection-dialog',
    templateUrl: './shared-application-parameters-selection-dialog.component.html',
    styleUrls: ['./shared-application-parameters-selection-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedApplicationParametersSelectionDialogComponent extends BaseComponent implements OnInit {

    categoriesList: { label: string, applicationParametersFormsList: UntypedFormGroup[] }[] = [];

    shouldDisplayDialogControl = new UntypedFormControl(false);

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: UntypedFormBuilder,
                private dialogRef: KalDialogRef<SharedApplicationParametersSelectionDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: SharedApplicationParametersSelectionDialogData,
                private readonly snackbarService: KalSnackbarService) {
        super(cdr);
    }

    static ShouldDisplayDialog(code: string, activityId: number = null): boolean {
        const store = InjectorProviderService.get(Store);
        return store.selectSnapshot(UserState.hasApplicationParameters)(code, activityId);
    }

    save(): void {
        if (this.loading) {
            return;
        }

        const params = this.categoriesList.map(category => {
            return {
                label: category.label,
                applicationParametersList: category.applicationParametersFormsList.map(code => code.value)
            };
        });

        const codesList: ParametresUserAppliModelDto[] = params.flatMap(e => e.applicationParametersList).map(code => {
            return {
                IdActivite: code.activityId,
                CodeChamp: code.code,
                CodeValeur: code.selected ? '1' : '0'
            };
        });

        const values = {
            ...this.data,
            params
        };

        this.loading = true;

        this.watch$ = this.dispatch$(new UserUpdateApplicationParametersAction(codesList)).pipe(
            tap(() => {
                this.snackbarService.open({title: 'Les paramètres d\'application ont bien été enregistrés'});
                this.dialogRef.close(values);
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private createCodeForm(code: SharedApplicationParameters): UntypedFormGroup {
        return this.fb.group({
            selected: [this.snapshot(UserState.hasApplicationParameters)(code.code, this.data.activityId)],
            activityId: [this.snapshot(UserState.getApplicationParameter)(code.code, this.data.activityId)?.IdActivite || null],
            code: [code.code],
            label: [code.label]
        });
    }

    ngOnInit(): void {
        if ('code' in this.data.params?.[0]) {
            this.categoriesList.push({
                label: 'Actions à effectuer',
                applicationParametersFormsList: (this.data.params as SharedApplicationParameters[]).map(code => this.createCodeForm(code))
            });
        } else {
            this.categoriesList = (this.data.params as SharedApplicationParametersCategory[])?.map(category => {
                return {
                    label: category.label,
                    applicationParametersFormsList: category.applicationParametersList.map(code => this.createCodeForm(code))
                };
            }) || [];
        }
    }

}
